"use client"

import { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap"
import { toast } from "react-toastify"
import Api from "../../api/Api"

const ProductDetailsModal = ({ modal, toggle, product, onStatusUpdate }) => {
  const [loading, setLoading] = useState(false)
  const [newStatus, setNewStatus] = useState(product?.status || "active")

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: "success", text: "Active" },
      inactive: { color: "secondary", text: "Inactive" },
      suspended: { color: "warning", text: "Suspended" },
      out_of_stock: { color: "danger", text: "Out of Stock" },
      pending: { color: "info", text: "Pending" },
    }

    const config = statusConfig[status] || { color: "secondary", text: status }
    return <Badge color={config.color}>{config.text}</Badge>
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const handleStatusUpdate = async () => {
    if (newStatus === product.status) {
      toast.info("Status is already set to " + newStatus)
      return
    }

    try {
      setLoading(true)
      const response = await Api.put(`/admin/products/${product.id}/status`, {
        status: newStatus,
      })

      if (response.data.success) {
        toast.success("Product status updated successfully")
        onStatusUpdate(product.id, newStatus)
        toggle()
      } else {
        toast.error(response.data.message || "Failed to update status")
      }
    } catch (error) {
      console.error("Update status error:", error)
      toast.error(error.response?.data?.message || "Failed to update status")
    } finally {
      setLoading(false)
    }
  }

  if (!product) return null

  return (
    <Modal isOpen={modal} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <i className="ri-shopping-bag-line me-2 text-primary"></i>
          Product Details
        </div>
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={4}>
            {product.image_url ? (
              <img
                src={product.image_url || "/placeholder.svg"}
                alt={product.title}
                className="img-fluid rounded"
                style={{ width: "100%", maxHeight: "200px", objectFit: "cover" }}
              />
            ) : (
              <div
                className="d-flex align-items-center justify-content-center bg-light rounded"
                style={{ height: "200px" }}
              >
                <i className="ri-image-line fs-1 text-muted"></i>
              </div>
            )}
          </Col>
          <Col md={8}>
            <div className="mb-3">
              <h5 className="mb-1">{product.title}</h5>
              {product.sku && <small className="text-muted">SKU: {product.sku}</small>}
            </div>

            <div className="mb-3">
              <strong>Price: </strong>
              <span className="fs-5 text-primary fw-bold">{formatPrice(product.price)}</span>
            </div>

            <div className="mb-3">
              <strong>Status: </strong>
              {getStatusBadge(product.status)}
            </div>

            <div className="mb-3">
              <strong>Stock Quantity: </strong>
              <span className={`fw-medium ${product.stock_quantity <= 5 ? "text-danger" : "text-success"}`}>
                {product.stock_quantity || 0} units
              </span>
            </div>

            <div className="mb-3">
              <strong>Category: </strong>
              <span>{product.category?.name || "No Category"}</span>
            </div>

            {product.weight && (
              <div className="mb-3">
                <strong>Weight: </strong>
                <span>{product.weight} kg</span>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="mb-3">
                <strong>Tags: </strong>
                {product.tags.map((tag, index) => (
                  <Badge key={index} color="light" className="me-1">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </Col>
        </Row>

        {product.description && (
          <Row className="mt-3">
            <Col>
              <Card>
                <CardBody>
                  <h6>Description</h6>
                  <p className="text-muted mb-0">{product.description}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col md={6}>
            <Card>
              <CardBody>
                <h6>Owner Information</h6>
                <div className="mb-2">
                  <strong>Name: </strong>
                  <span>{product.user?.name}</span>
                </div>
                <div className="mb-2">
                  <strong>Email: </strong>
                  <span>{product.user?.email}</span>
                </div>
                <div className="mb-2">
                  <strong>User Type: </strong>
                  <Badge color="info">
                    {product.user?.account_type_id === 1
                      ? "Influencer"
                      : product.user?.account_type_id === 2
                        ? "Business"
                        : "User"}
                  </Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <CardBody>
                <h6>Product Information</h6>
                <div className="mb-2">
                  <strong>Created: </strong>
                  <span>{formatDate(product.created_at)}</span>
                </div>
                <div className="mb-2">
                  <strong>Updated: </strong>
                  <span>{formatDate(product.updated_at)}</span>
                </div>
                <div className="mb-2">
                  <strong>Featured: </strong>
                  <Badge color={product.featured ? "success" : "secondary"}>{product.featured ? "Yes" : "No"}</Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <Card>
              <CardBody>
                <h6>Update Product Status</h6>
                <Form>
                  <FormGroup>
                    <Label for="statusSelect">Status</Label>
                    <Input
                      type="select"
                      id="statusSelect"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                      <option value="out_of_stock">Out of Stock</option>
                      <option value="pending">Pending</option>
                    </Input>
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
        <Button color="primary" onClick={handleStatusUpdate} disabled={loading || newStatus === product.status}>
          {loading ? (
            <>
              <i className="ri-loader-2-line me-1"></i>
              Updating...
            </>
          ) : (
            <>
              <i className="ri-save-line me-1"></i>
              Update Status
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ProductDetailsModal
